import { IssueAge, IssueCounts } from '../../api/issues';

export interface IStatusFilterProps {
    counts: IssueCounts | null;
    status: string;
    age: IssueAge;
    maxAgeDays: number;
    onStatus: (status: string) => void;
    onAge: (age: IssueAge) => void;
}

interface Button {
    status: string;
    label: string;
    tone: string;
    count: (c: IssueCounts) => number | null | undefined;
    hint: string;
}

const STATUS_BUTTONS: Button[] = [
    { status: 'open', label: 'Alle open', tone: 'info', count: (c) => c.open, hint: 'Alles wat nog niet is opgelost' },
    { status: 'WaitingOnUs', label: 'Bij ons', tone: 'error', count: (c) => c.waitingOnUs, hint: 'Wij moeten iets doen' },
    { status: 'WaitingOnCustomer', label: 'Bij klant', tone: 'warn', count: (c) => c.waitingOnCustomer, hint: 'De klant moet iets aanpassen' },
    { status: 'NeedsRequeue', label: 'Opnieuw aanbieden', tone: 'info', count: (c) => c.needsRequeue, hint: 'Een nieuwe poging lost het op' },
    { status: 'ResolvesItself', label: 'Gaat vanzelf', tone: 'muted', count: (c) => c.resolvesItself, hint: 'De connector probeert het zelf nog' },
    { status: 'resolved', label: 'Opgelost', tone: 'ok', count: (c) => c.resolved, hint: 'Opgelost of met de hand gesloten' },
];

const isOpenStatus = (status: string) => status !== 'resolved' && status !== 'all' && status !== 'Closed';

/**
 * The statuses as buttons with their numbers: one row that is both the count and the filter,
 * so the number on a button is what clicking it shows. Underneath the same for the age.
 */
export const StatusFilter = ({ counts, status, age, maxAgeDays, onStatus, onAge }: IStatusFilterProps) => {
    const ages: { age: IssueAge; label: string; count: number | null; hint: string }[] = [
        { age: 'recent', label: 'Recent', count: counts?.recent ?? null, hint: `Eerste fout in de laatste ${maxAgeDays} dagen` },
        { age: 'backlog', label: 'Achterstand', count: counts?.backlog ?? null, hint: `Eerste fout langer dan ${maxAgeDays} dagen geleden` },
        { age: '', label: 'Alle leeftijden', count: counts ? counts.recent + counts.backlog : null, hint: 'Recent en achterstand samen' },
    ];

    return (
        <div className="status-filter">
            <div className="status-filter__row" role="group" aria-label="Status">
                {STATUS_BUTTONS.map((b) => {
                    const value = counts ? b.count(counts) : null;
                    const active = status === b.status;

                    return (
                        <button
                            key={b.status}
                            type="button"
                            className={`status-filter__button status-filter__button--${value === 0 ? 'muted' : b.tone}${active ? ' is-active' : ''}`}
                            aria-pressed={active}
                            title={b.hint}
                            onClick={() => onStatus(b.status)}
                        >
                            <strong>{value === null || value === undefined ? '–' : value.toLocaleString('nl-NL')}</strong>
                            <span>{b.label}</span>
                        </button>
                    );
                })}
            </div>
            {isOpenStatus(status) && (
                <div className="status-filter__row status-filter__row--age" role="group" aria-label="Leeftijd">
                    {ages.map((a) => {
                        const active = age === a.age;

                        return (
                            <button
                                key={a.age || 'all'}
                                type="button"
                                className={`status-filter__age${active ? ' is-active' : ''}`}
                                aria-pressed={active}
                                title={a.hint}
                                onClick={() => onAge(a.age)}
                            >
                                {a.label}
                                {a.count !== null && <span className="status-filter__age-count">{a.count.toLocaleString('nl-NL')}</span>}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
